import { useQuery } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import type {
  ViewConfig,
  EntityRow,
  EntityCellValue,
  ProcessView,
} from "../Dashboard/types";
import type { OrgFundMetrics, OrgFundReturns } from "./performance";
import {
  isUnifiedSearchResponse,
  unifiedToLegacyRows,
} from "./searchResponseAdapter";
import { filterBoundDay, viewerTimeZone } from "@/utils/temporal";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";
import {
  keyForSource,
  useRecordDataSource,
  type RecordDataSource,
} from "../Dashboard/recordDataSource";

// Search parameters interface
// The one way a search term can be applied other than the default relevance
// re-rank (#11804): a case-insensitive contains match that narrows.
export type NameMatch = "contains";

// The Board view's search narrows with a contains match (#11804) — but only
// where the endpoint implements `name_match`: the entity search, count and
// CSV routes. The interactions and documents routes accept the field and
// ignore it, so a board over those collections keeps the ranked search and
// must not claim "matching" in its footer. Same set the server CSV path
// serves (exportTable.canUseServerCsv).
export const boardNameMatch = (view: ViewConfig): NameMatch | undefined =>
  view.type === "search" && view.entityKind !== "interaction"
    ? "contains"
    : undefined;

export interface SearchParams {
  viewConfig: ViewConfig;
  filter?: string;
  columns?: string[];
  keyset_id?: string | null;
  page_size?: number;
  // Items to skip after `keyset_id`, to jump pages (#10593). Measured
  // from the cursor, so a jump costs its distance rather than its depth:
  // to reach page T from page C, send page C's last-row cursor and
  // (T - C - 1) * page_size. A cold jump sends no cursor and
  // (T - 1) * page_size. Counts groups when `group_by` is set.
  page_offset?: number;
  as_of?: Date;
  name?: string;
  // How `name` is applied (#11804). Omitted, a name search is the table's
  // relevance re-rank (#8908). "contains" is the board's narrowing: only
  // records whose name, alias, or (vehicles) manager name contains the
  // term, in the caller's own sort.
  name_match?: NameMatch;
  sort_by?: string[];
  sort_order?: ("asc" | "desc")[];
  is_export?: boolean;
  // Column names to group rows by. Page size counts groups (not rows)
  // when non-empty; rows within each group come back clustered with a
  // `group_rank` field for client-side display.
  group_by?: string[];
  // Grouped views only: at most `group_row_limit` rows per group, each row
  // carrying its group's full `group_size`. `group_row_limits` raises that for
  // single groups of the page, keyed by `group_rank`: the grid's "Show more",
  // part of the page so a refetch returns the same rows.
  group_row_limit?: number;
  group_row_limits?: Record<number, number>;
  // Grouped views only: a `group_by` column grouped by a bucket rather than
  // its own value (#13370, round 30). "year" takes a date to its calendar
  // year, "each" files a row under each value of a multi-valued column
  // (Document Type). Each bucket's key comes back as `group_value` (first
  // level) or `subgroup_value` (second). The aggregate search honours it.
  group_buckets?: Record<string, "year" | "each">;
}

// Query configuration constants
export const SEARCH_STALE_TIME = 2 * 60 * 1000; // 2 minutes - allows showing stale data when navigating back
export const SEARCH_GC_TIME = 10 * 60 * 1000; // 10 minutes - keep in cache longer for navigation

// Query Keys
export const SEARCH_QUERY_KEYS = {
  searchEntities: (params: SearchParams) =>
    ["rms", "search", JSON.stringify(params)] as const,
};

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatInceptionDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return dateStr;
  return `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// Transform org performance metrics into EntityRow[] for the shared table
export function transformPerformanceToEntityRows(
  data: OrgFundMetrics[] | OrgFundReturns[],
  timePeriod: string
): EntityRow[] {
  if (timePeriod === "annual") {
    return (data as OrgFundMetrics[]).map((fund) => {
      const row: Record<string, EntityCellValue> = {};
      const metricKeys = [
        "one_month_return",
        "quarter_to_date_return",
        "year_to_date_return",
        "one_year_return",
        "three_year_return",
        "five_year_return",
        "seven_year_return",
        "ten_year_return",
        "inception_to_date_return",
      ] as const;
      for (const key of metricKeys) {
        const val = fund[key];
        // Divide by 100: backend returns percentages (e.g. -1.70 for -1.70%),
        // but ScalarValueRenderer expects decimals (e.g. -0.017) and multiplies by 100
        row[key] = { scalar: val != null ? val / 100 : null, entity: null };
      }
      row["inception_date"] = {
        scalar: formatInceptionDate(fund.inception_date),
        entity: null,
      };
      row["labels"] = fund.labels.map((l) => ({ scalar: l, entity: null }));
      row["overridden_periods"] = fund.overridden_periods.map((p) => ({
        scalar: p,
        entity: null,
      }));
      row["_entity_public_id"] = {
        scalar: fund.entity_public_id,
        entity: null,
      };
      row["_entity_name"] = { scalar: fund.entity_name, entity: null };
      row["_is_benchmark"] = {
        scalar: fund.labels.includes("Benchmark"),
        entity: null,
      };
      row["_is_hidden"] = { scalar: fund.show === false, entity: null };
      return {
        id: fund.fund_id,
        keyset_id: fund.fund_id,
        name: fund.fund_name || fund.fund_id,
        data: row,
      };
    });
  }

  // Monthly / Quarterly
  return (data as OrgFundReturns[]).map((fund) => {
    const row: Record<string, EntityCellValue> = {};
    for (const r of fund.returns) {
      // Divide by 100: same reason as above
      row[r.date] = { scalar: r.value / 100, entity: null };
    }
    row["labels"] = fund.labels.map((l) => ({ scalar: l, entity: null }));
    row["_entity_public_id"] = {
      scalar: fund.entity_public_id,
      entity: null,
    };
    row["_entity_name"] = { scalar: fund.entity_name, entity: null };
    row["_is_benchmark"] = {
      scalar: fund.labels.includes("Benchmark"),
      entity: null,
    };
    row["_is_hidden"] = { scalar: fund.show === false, entity: null };
    return {
      id: fund.fund_id,
      keyset_id: fund.fund_id,
      name: fund.fund_name || fund.fund_id,
      data: row,
    };
  });
}

// Fetch performance data and return as EntityRow[]
async function fetchPerformanceAsEntityRows(
  params: SearchParams
): Promise<EntityRow[]> {
  if (params.viewConfig.type !== "performance") return [];
  const { asOfDate, timePeriod = "annual" } = params.viewConfig;

  const urlParams = new URLSearchParams({ return_type: "net" });
  if (asOfDate) urlParams.append("as_of_date", asOfDate);
  urlParams.append("time_period", timePeriod);
  if (params.name) urlParams.append("name", params.name);
  if (params.keyset_id) urlParams.append("keyset_id", params.keyset_id);
  if (params.page_size != null)
    urlParams.append("page_size", String(params.page_size));
  if (params.is_export) urlParams.append("is_export", "true");
  if (params.sort_by) {
    for (const sb of params.sort_by) urlParams.append("sort_by", sb);
  }
  if (params.sort_order) {
    for (const so of params.sort_order) urlParams.append("sort_order", so);
  }

  // Extract labels and toggle filters from the filters JSON
  if (params.filter) {
    try {
      const filters = JSON.parse(params.filter) as {
        name: string;
        op: string;
        value: any;
      }[];
      const labelsFilter = filters.find((f) => f.name === "labels");
      if (labelsFilter && Array.isArray(labelsFilter.value)) {
        for (const label of labelsFilter.value) {
          urlParams.append("labels", label);
        }
      }
      const showHiddenFilter = filters.find((f) => f.name === "show_hidden");
      if (showHiddenFilter && typeof showHiddenFilter.value === "boolean") {
        urlParams.append("show_hidden", String(showHiddenFilter.value));
      }
      const showBenchmarkFilter = filters.find(
        (f) => f.name === "show_benchmark"
      );
      if (
        showBenchmarkFilter &&
        typeof showBenchmarkFilter.value === "boolean"
      ) {
        urlParams.append("show_benchmark", String(showBenchmarkFilter.value));
      }
    } catch {
      // ignore parse errors
    }
  }

  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/performance/org-metrics?${urlParams}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch performance metrics: ${response.status}`);
  }

  const data = await response.json();
  return transformPerformanceToEntityRows(data.results, timePeriod);
}

// Fetch activity-log rows and adapt to EntityRow[] for the shared table.
// Request body mirrors /rms/entities/search so filters / sort / pagination
// flow through the standard RMS search machinery untouched. Shared with the
// activity count so the two agree on what matches (#10593).
function activityBody(params: SearchParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    user_tz: currentViewerTimeZone(),
  };
  if (params.filter) body.filter = params.filter;
  if (params.sort_by) body.sort_by = params.sort_by;
  if (params.sort_order) body.sort_order = params.sort_order;
  if (params.group_by) body.group_by = params.group_by;
  if (params.keyset_id !== undefined) body.keyset_id = String(params.keyset_id);
  if (params.page_size !== undefined) body.page_size = params.page_size;
  if (params.page_offset) body.page_offset = params.page_offset;
  return body;
}

// A process view's endpoint takes the SAME body as /rms/activity-log/search —
// the shared search contract — so there is nothing to translate: filter JSON,
// sort arrays, column projection and keyset page all pass through untouched.
// That equality is the point of a process view; a bespoke body here would
// mean a bespoke grid over there.
export function processBody(params: SearchParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (params.filter) body.filter = params.filter;
  if (params.sort_by) body.sort_by = params.sort_by;
  if (params.sort_order) body.sort_order = params.sort_order;
  if (params.columns && params.columns.length > 0)
    body.columns = params.columns;
  if (params.keyset_id !== undefined) body.keyset_id = String(params.keyset_id);
  if (params.page_size !== undefined) body.page_size = params.page_size;
  if (params.page_offset) body.page_offset = params.page_offset;
  if (params.name) body.name = params.name;
  // The Export control asks for every row in one request. Without this the
  // backend applies the ordinary page ceiling and the CSV is silently short —
  // #11126 on a new surface.
  if (params.is_export) body.is_export = true;
  return body;
}

async function fetchProcessAsEntityRows(
  params: SearchParams,
  view: ProcessView
): Promise<EntityRow[]> {
  const response = await fpFetch(`${BASE_API_URL_V2}${view.searchEndpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...view.headers() },
    body: JSON.stringify(processBody(params)),
  });

  if (!response.ok) {
    throw new Error(`Failed to search ${view.kind}: ${response.status}`);
  }

  const data = await response.json();
  return unifiedToLegacyRows(data, [], view.companionCells);
}

async function fetchActivityAsEntityRows(
  params: SearchParams
): Promise<EntityRow[]> {
  // Call the activity-log endpoint directly — it returns the unified
  // {columns, rows} shape, which searchResponseAdapter flattens to EntityRow[].
  // The audit-log-specific extras (fact_entries, review state, bulk fields,
  // details_before/after) ride on Row.activity_hack and the adapter
  // surfaces them under __hack__ keys for FactChangesModal /
  // ActivityFactEntriesCell to consume.
  const body = activityBody(params);

  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/activity-log/search`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch activity log: ${response.status}`);
  }

  const data = await response.json();
  return unifiedToLegacyRows(data, params.group_by);
}

// Fetch the unified Document-review grid (staged documents + interactions
// awaiting Bipsync sync) and adapt to EntityRow[] for the shared table.
// The endpoint returns the unified {columns, rows} shape with per-row
// provenance overrides; searchResponseAdapter threads those onto
// EntityRow.cellProvenance so editability/write-routing resolves per row.
// Supports the same filter / sort / keyset pagination as the other search
// views (computed in Python over the staged union, server-side).
// Translate the grid's FrontendFilter[] JSON into the document-review
// endpoint's `{column, op, values}[]` contract, preserving the clause's `op` so
// the backend can tell a date range from a value list and an entity-id match
// from a substring match.
//
// A multi-select or entity-picker clause carries an array value (Status =
// ["pending", "synced"], Managers = [entity ids]); its values OR together
// within the column and AND across columns. A `between` clause carries exactly
// [lo, hi] calendar days, either of which may be open -- they keep their
// positions, so an open bound stays open. Everything else is a single value.
// Empty / null values are dropped; malformed JSON yields no filters (rather
// than a 400).
//
// The bounds go over as the bare days the clause stores (#11055). Only a clause
// saved before that needs `filterBoundDay` to do anything, which is why it
// takes the viewer's zone: recovering the day out of a frozen local midnight is
// the one thing that cannot be done without one.
export type DocReviewFilter = {
  column: string;
  op: string;
  values: Array<string | null>;
  // "Is not", "does not contain", "is none of": the rows the clause does not
  // select, blank ones included (#13416). Sent only when set.
  negate?: true;
};

export function docReviewFilters(
  filterJson: string,
  viewerZone: string
): DocReviewFilter[] {
  let frontendFilters: Array<{
    name: string;
    op?: string;
    value?: unknown;
    negate?: boolean;
  }>;
  try {
    frontendFilters = JSON.parse(filterJson);
  } catch {
    return [];
  }
  const filters: DocReviewFilter[] = [];
  for (const f of frontendFilters) {
    const op = f.op ?? "similar";
    const negation: { negate?: true } = f.negate ? { negate: true } : {};
    // Presence clauses (#11243) carry no values by design — the operator is the
    // whole clause. They must survive the "drop empty clauses" rule below, or
    // the review grid silently returns an unfiltered list.
    if (op === "is_blank" || op === "is_not_blank") {
      filters.push({ column: f.name, op, values: [], ...negation });
      continue;
    }
    if (op === "between") {
      const [lo, hi] = Array.isArray(f.value) ? f.value : [null, null];
      const bounds = [
        filterBoundDay(lo, viewerZone),
        filterBoundDay(hi, viewerZone),
      ];
      if (bounds[0] === null && bounds[1] === null) continue;
      filters.push({ column: f.name, op, values: bounds, ...negation });
      continue;
    }
    const raw = Array.isArray(f.value) ? f.value : [f.value];
    const values = raw
      .filter((v) => v !== undefined && v !== null && v !== "")
      .map((v) => String(v));
    if (values.length === 0) continue;
    filters.push({ column: f.name, op, values, ...negation });
  }
  return filters;
}

// The document-review endpoint takes its OWN request shape (it filters /
// sorts the staged union in Python), NOT the standard search machinery's
// `filter` JSON string + `sort_by`/`sort_order` arrays the other RMS views
// pass through. Translate to its contract: `filters: [{column, op, values}]`
// and a multi-column `sort_by` + `sort_dir`. Shared with its count (#10593).
function docReviewBody(params: SearchParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    user_tz: currentViewerTimeZone(),
  };

  // Map the grid's FrontendFilter[] JSON to the endpoint's {column, op, values}
  // clauses (see docReviewFilters). Only non-empty clauses are sent; the backend
  // ORs values within a column and ANDs across columns.
  if (params.filter) {
    const filters = docReviewFilters(params.filter, viewerTimeZone());
    if (filters.length > 0) body.filters = filters;
  }

  // Multi-column sort: the endpoint takes a column priority list + an aligned
  // direction list (each spec honored, ties broken by later specs). Pass the
  // grid's sort array through as-is.
  if (params.sort_by && params.sort_by.length > 0) {
    body.sort_by = params.sort_by;
    if (params.sort_order) body.sort_dir = params.sort_order;
  }

  // Free-text search bar: `params.name` carries the grid's search text (set by
  // createQueryParams from searchState.searchText). The endpoint matches it as a
  // case-insensitive substring over each row's name, linked entity names, and
  // author. Only send a non-empty trimmed term.
  if (params.name && params.name.trim() !== "") {
    body.search = params.name;
  }

  if (params.keyset_id !== undefined) body.keyset_id = String(params.keyset_id);
  if (params.page_size !== undefined) body.page_size = params.page_size;
  if (params.page_offset) body.page_offset = params.page_offset;
  return body;
}

async function fetchDocumentReviewAsEntityRows(
  params: SearchParams
): Promise<EntityRow[]> {
  const body = docReviewBody(params);

  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/review/document-review/search`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch document review: ${response.status}`);
  }

  const data = await response.json();
  return unifiedToLegacyRows(data);
}

// API function to search entities
// Uses POST because filter parameters can exceed URL length limits (gunicorn
// 4094 bytes, AWS ALB 8KB hard cap). Conceptually this is a read-only search,
// but the filter payload is too large for query strings.
//
// `signal` comes from React Query's queryFn so superseded searches cancel
// the in-flight fetch — saves backend cycles when users change filters
// faster than responses come back.
// Body for the four views that share the standard search machinery
// (entities / interactions / documents / shadowbook). Shared with their
// counts so the two agree on what matches (#10593).
function unifiedBody(params: SearchParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  // Entity search requires kind; interaction/aggregation search doesn't use it
  if (
    params.viewConfig.type === "search" &&
    params.viewConfig.entityKind !== "interaction"
  ) {
    body.kind = params.viewConfig.entityKind;
  }

  if (params.filter) body.filter = params.filter;

  // Entity scoping for the entity page's table renderings (#10552). Applied
  // here, at request-build time, via the `scope_entity_public_id` parameter
  // rather than by pushing onto the search store's `filters` — that array is
  // user-facing and user-removable, and a scope the user could delete would
  // reveal the whole org on an entity page. The parameter also lets the
  // backend express the entity list's "record reachable through ANY
  // relationship bucket" rule, which the AND-ed filter grammar cannot.
  //
  // Both tables take this route. The documents table additionally forwards the
  // list's two toggles (vehicle docs / interaction docs); the backend widens
  // the scope to match the list's basis for whichever are on.
  //
  // Lives in the shared body rather than the search's own, so a scoped view's
  // count measures the same rows the table shows (#10593).
  // The transactions ledger scopes the same way (#10760 slice 7) — its tab on a
  // vehicle's page is the same grid narrowed to that vehicle's linked Caissa
  // funds. It carries no widening toggles: a ledger line belongs to one fund or
  // to none, so there is no second relationship bucket to opt into.
  const scope =
    params.viewConfig.type === "search" ||
    params.viewConfig.type === "aggregation" ||
    params.viewConfig.type === "transactions" ||
    params.viewConfig.type === "holdings" ||
    params.viewConfig.type === "dataset" ||
    params.viewConfig.type === "tasks"
      ? params.viewConfig.scope
      : undefined;
  if (scope) {
    body.scope_entity_public_id = scope.publicId;
    if (
      params.viewConfig.type === "aggregation" &&
      params.viewConfig.panelKind === "document"
    ) {
      if (scope.includeVehicleDocuments) body.include_vehicle_documents = true;
    } else if (
      params.viewConfig.type === "search" &&
      params.viewConfig.entityKind === "interaction"
    ) {
      if (scope.participantOnly) body.scope_participant_only = true;
      // Passing mentions are retired (#10406): the backend hides passing-only
      // rows unconditionally, so no passing scope param is sent.
    }
  }

  if (params.columns && params.columns.length > 0)
    body.columns = params.columns;
  if (params.keyset_id !== undefined) body.keyset_id = String(params.keyset_id);
  if (params.page_size !== undefined) body.page_size = params.page_size;
  if (params.page_offset) body.page_offset = params.page_offset;
  if (params.as_of) body.as_of = params.as_of.toISOString();
  // The Holdings view's point-in-time date (#10835). Sent as a plain
  // YYYY-MM-DD string off the view config; omitted, the backend defaults to the
  // latest snapshot available.
  if (params.viewConfig.type === "holdings" && params.viewConfig.asOfDate)
    body.as_of_date = params.viewConfig.asOfDate;
  // Which dataset a dataset view reads (#12178). Always sent: without it the
  // request names no rows at all. Rides into the count too, since the count
  // body is this one minus its paging.
  if (params.viewConfig.type === "dataset") {
    body.dataset_id = params.viewConfig.datasetId;
    // Which versions of the rows (#12498): neither is the current ones. The
    // day goes as a calendar day; the server reads it through its last moment
    // in `user_tz`.
    const versions = params.viewConfig.versions;
    if (versions?.kind === "history") body.include_history = true;
    if (versions?.kind === "asOfDay") body.as_of_day = versions.day;
  }
  if (params.name) body.name = params.name;
  if (params.name && params.name_match) body.name_match = params.name_match;
  if (params.sort_by) body.sort_by = params.sort_by;
  if (params.sort_order) body.sort_order = params.sort_order;
  if (params.group_by && params.group_by.length > 0) {
    body.group_by = params.group_by;
    if (params.group_row_limit) body.group_row_limit = params.group_row_limit;
    if (params.group_row_limits)
      body.group_row_limits = params.group_row_limits;
    if (params.group_buckets) body.group_buckets = params.group_buckets;
  }
  if (params.is_export) body.is_export = true;
  if (params.viewConfig.type === "aggregation" && params.viewConfig.flat)
    body.flat = true;
  // Browser tz so the backend buckets DATE-typed sort keys in the same
  // tz the cell renderer uses for display (FactRenderer's local-time
  // getDate). Without this, occurred_at near UTC midnight buckets one
  // day off from what the user sees.
  body.user_tz = currentViewerTimeZone();

  return body;
}

function unifiedSearchEndpoint(view: ViewConfig): string {
  if (view.type === "aggregation") {
    return view.panelKind === "shadowbook"
      ? "/api/v2/rms/documents/search/shadowbook"
      : "/api/v2/rms/documents/search";
  }
  if (view.type === "search" && view.entityKind === "interaction") {
    return "/api/v2/rms/interactions/search";
  }
  // The Caissa ledger (#10760 slice 6) reads the standard body — filter JSON,
  // sort, group, keyset page — so it needs an endpoint here and nothing else.
  if (view.type === "transactions") {
    return "/api/v2/rms/transactions/search";
  }
  // The global Holdings view (#10835) reads the standard body — filter JSON,
  // sort, keyset page — so it needs an endpoint here and nothing else.
  if (view.type === "holdings") {
    return "/api/v2/rms/holdings/search";
  }
  // A dataset view (#12178) reads the standard body plus `dataset_id`.
  if (view.type === "dataset") {
    return "/api/v2/rms/datasets/search";
  }
  // Tasks (#11942 round 21) speak the standard body too.
  if (view.type === "tasks") {
    return "/api/v2/work/tasks/search";
  }
  return "/api/v2/rms/entities/search";
}

// The body a view's search sends. Counts reuse this and strip the page
// window, so a count can't read a different filter set than the table.
function searchBodyFor(params: SearchParams): Record<string, unknown> {
  switch (params.viewConfig.type) {
    case "activity":
      return activityBody(params);
    case "document_review":
      return docReviewBody(params);
    case "process":
      return processBody(params);
    default:
      return unifiedBody(params);
  }
}

/** A grid search's answer: its rows, and on the holdings search the
 *  totals of its number and percentage columns over every row it matches
 *  (#14991), so the totals row reads the grid's own answer. */
export interface SearchPage {
  rows: EntityRow[];
  /** By column name; null where the search sums nothing. */
  columnTotals: Record<string, number> | null;
}

const rowsOnly = (rows: EntityRow[]): SearchPage => ({
  rows,
  columnTotals: null,
});

export const searchEntitiesPage = async (
  params: SearchParams,
  signal?: AbortSignal
): Promise<SearchPage> => {
  // Performance views use a separate endpoint and data transformation
  if (params.viewConfig.type === "performance") {
    return rowsOnly(await fetchPerformanceAsEntityRows(params));
  }

  // Activity view: the compiler-driven audit feed.
  if (params.viewConfig.type === "activity") {
    return rowsOnly(await fetchActivityAsEntityRows(params));
  }

  // Document-review view: the unified staged documents + interactions grid.
  if (params.viewConfig.type === "document_review") {
    return rowsOnly(await fetchDocumentReviewAsEntityRows(params));
  }

  if (params.viewConfig.type === "process") {
    return rowsOnly(
      await fetchProcessAsEntityRows(params, params.viewConfig.view)
    );
  }

  const body = unifiedBody(params);
  const endpoint = unifiedSearchEndpoint(params.viewConfig);

  const response = await fpFetch(`${BASE_API_URL_V2}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to search entities: ${response.status}`);
  }

  const data = await response.json();
  // The four search endpoints (entities/interactions/documents/shadowbook
  // /activity-log) return UnifiedSearchResponse{columns, rows}. The
  // adapter flattens each row's RowCell map into a legacy EntityRow.data
  // dict so TablePanel + cell renderers don't change. Schema for these
  // views arrives via /rms/schema/{kind} + bootstrap.ts directly in
  // legacy shape — no schema-side adapter needed.
  if (isUnifiedSearchResponse(data)) {
    const totals: Record<string, number> | null | undefined =
      data.column_totals;
    return {
      rows: unifiedToLegacyRows(data, params.group_by),
      columnTotals: totals ?? null,
    };
  }
  return rowsOnly(data as EntityRow[]);
};

/** A grid search's rows alone. */
export const searchEntitiesApi = async (
  params: SearchParams,
  signal?: AbortSignal
): Promise<EntityRow[]> => (await searchEntitiesPage(params, signal)).rows;

/**
 * A search's key as a data source reads it (#13370, round 36b): the API's key unchanged, another
 * source's (Appearance's demo record) set apart, so a demo row never answers a record page's read.
 */
export const searchQueryKeyFor = (
  params: SearchParams,
  source: RecordDataSource
): readonly unknown[] =>
  keyForSource(source, SEARCH_QUERY_KEYS.searchEntities(params));

// Search Query Hook. A record's tab reads through the data source its host provides (the API's
// unless Appearance draws it on the demo record; spec section 23).
export const useSearchEntities = (
  params: SearchParams,
  enabled: boolean = true
) => {
  const source = useRecordDataSource();
  return useQuery({
    queryKey: searchQueryKeyFor(params, source),
    queryFn: ({ signal }) => source.search(params, signal),
    enabled: enabled && !!params.viewConfig,
    staleTime: SEARCH_STALE_TIME,
    gcTime: SEARCH_GC_TIME,
  });
};

// ───────────────────────────────────────────────────────────────────
// View counts (#8690, extended to every view in #10593)
//
// Deliberately isolated from the search query above: the table panel
// queries the count endpoint independently so a slow or failed count
// never blocks or errors the main data load.
//
// Each count reuses the *same body builder* as the search it mirrors,
// with pagination and projection stripped. That's load-bearing: keying
// the count on a hand-picked subset of the params is what let it drift
// from the table (it ignored `name`, so a relevance-cutoff search showed
// a handful of rows under the unfiltered total — #10593).
// ───────────────────────────────────────────────────────────────────

export interface ViewCount {
  // Rows matching the current filters.
  count: number;
  // Groups matching, when the view is grouped. Pages are measured in
  // groups then, so this — not `count` — divides into the page count.
  groupCount: number | null;
}

export const VIEW_COUNT_QUERY_KEYS = {
  count: (endpoint: string, body: Record<string, unknown>) =>
    ["rms", "count", endpoint, JSON.stringify(body)] as const,
};

// The count endpoint mirroring each view's search endpoint. `performance`
// has no count: it's a separate data path with its own endpoint rather
// than an RMS search, so there's nothing to count against. Views without
// a count fall back to forward-only paging.
export const countEndpointFor = (view: ViewConfig): string | null => {
  switch (view.type) {
    case "aggregation":
      return view.panelKind === "shadowbook"
        ? "/api/v2/rms/documents/count/shadowbook"
        : "/api/v2/rms/documents/count";
    case "search":
      return view.entityKind === "interaction"
        ? "/api/v2/rms/interactions/count"
        : "/api/v2/rms/entities/count";
    case "activity":
      return "/api/v2/rms/activity-log/count";
    case "document_review":
      return "/api/v2/rms/review/document-review/count";
    case "process":
      return view.view.countEndpoint;
    case "transactions":
      return "/api/v2/rms/transactions/count";
    case "holdings":
      return "/api/v2/rms/holdings/count";
    case "dataset":
      return "/api/v2/rms/datasets/count";
    case "tasks":
      return "/api/v2/work/tasks/count";
    case "performance":
      return null;
  }
};

// The noun for the count label. Frontend rule: never the word "entity" —
// name the kind the user is looking at.
export const countLabelFor = (view: ViewConfig): string | null => {
  switch (view.type) {
    case "search":
      return pluralizeKind(view.entityKind);
    case "aggregation":
      return "documents";
    case "activity":
      return "events";
    case "document_review":
      return "records";
    case "process":
      return view.view.countLabel;
    case "transactions":
      return "transactions";
    case "holdings":
      return "holdings";
    case "dataset":
      // A dataset's rows have no noun of their own; the view's name says what
      // they are.
      return "rows";
    case "tasks":
      return "tasks";
    case "performance":
      return null;
  }
};

// Exported so the footer sentence's singularizer derives from this map
// rather than hand-maintaining its inverse (collectionChrome.ts).
export const IRREGULAR_PLURALS: Record<string, string> = {
  person: "people",
  company: "companies",
};

export const pluralizeKind = (kind: string): string =>
  IRREGULAR_PLURALS[kind] ?? `${kind}s`;

// A count body is its view's search body minus the page window and the
// projection — everything that selects *which* rows match is kept.
//
// Sort is dropped unless a name search is active. A sort can't change how
// many rows match, with one exception: alongside a name search it turns
// the search into a relevance cutoff (#8908), which does narrow the set.
// So it's kept exactly when it's load-bearing. Dropping it otherwise
// keeps #8690's contract that re-sorting never refetches the count —
// otherwise every column click costs a COUNT(DISTINCT ...) on every view.
export const countBodyFor = (params: SearchParams): Record<string, unknown> => {
  const body = searchBodyFor(params);
  delete body.keyset_id;
  delete body.page_size;
  delete body.page_offset;
  delete body.columns;
  delete body.is_export;
  // How many rows of each group the page shows says nothing about which rows
  // match, so it never reaches the count.
  delete body.group_row_limit;
  delete body.group_row_limits;
  if (!params.name) {
    delete body.sort_by;
    delete body.sort_order;
    delete body.sort_dir;
  }
  // With no sort, a search on these views only ranks the rows (#8908), so
  // the name cannot change how many there are; leaving it out keeps one count
  // per filter state instead of one per keystroke (#14678). A sort turns the
  // search into a relevance cutoff, and the entity search always narrows by
  // name, so both keep it. A blank term matches nothing, so it keeps the
  // count at 0 alongside the empty page.
  const sorted = Array.isArray(body.sort_by) && body.sort_by.length > 0;
  const blank = typeof body.name === "string" && body.name.trim() === "";
  if (!sorted && !blank && searchOnlyRanks(params.viewConfig)) {
    delete body.name;
    delete body.name_match;
  }
  return body;
};

const searchOnlyRanks = (view: ViewConfig): boolean =>
  view.type === "aggregation" ||
  (view.type === "search" && view.entityKind === "interaction");

export const fetchViewCount = async (
  params: SearchParams,
  signal?: AbortSignal
): Promise<ViewCount> => {
  const endpoint = countEndpointFor(params.viewConfig);
  if (endpoint === null) throw new Error("view has no count endpoint");

  const response = await fpFetch(`${BASE_API_URL_V2}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // A process view's count asks the same way its rows do.
      ...(params.viewConfig.type === "process"
        ? params.viewConfig.view.headers()
        : {}),
    },
    body: JSON.stringify(countBodyFor(params)),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to count: ${response.status}`);
  }

  const data = await response.json();
  return {
    count: data.count as number,
    groupCount: (data.group_count as number | null) ?? null,
  };
};

// Count hook, driven by the same SearchParams as the search hook so the
// filter serialization is identical by construction. Disabled — and
// harmless — on views without a count endpoint.
// The one derivation of a count query's key, so a caller that patches the
// cache (the board's drop) names exactly the entry the hook reads.
export const viewCountQueryKey = (params: SearchParams) =>
  VIEW_COUNT_QUERY_KEYS.count(
    countEndpointFor(params.viewConfig) ?? "",
    countBodyFor(params)
  );

export const useViewCount = (params: SearchParams, enabled: boolean = true) => {
  const endpoint = countEndpointFor(params.viewConfig);
  // Counted by the same source the rows come from (#13370, round 36b).
  const source = useRecordDataSource();
  return useQuery({
    queryKey: keyForSource(source, viewCountQueryKey(params)),
    queryFn: ({ signal }) => source.count(params, signal),
    enabled: enabled && endpoint !== null,
    staleTime: SEARCH_STALE_TIME,
    gcTime: SEARCH_GC_TIME,
  });
};
